import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  Collapse,
  IconButton,
  Grid,
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Info as InfoIcon,
} from '@mui/icons-material';
import { fetchOpportunities, type OpportunityListResponse } from '../../services/analytics.service';
import { useAuthStore } from '../../store/auth.store';

export default function OpportunityAnalyticsPage() {
  const user = useAuthStore((state) => state.user);
  // Use effective tenant (proxy if switched, else home)
  const effectiveTenantId = user?.proxyTenantId || user?.tenantId;
  const tenantId = effectiveTenantId ? Number(effectiveTenantId) : 1;
  const [minImpressions, setMinImpressions] = useState<number | ''>(10);
  const [levelFilter, setLevelFilter] = useState<'HIGH' | 'MEDIUM' | 'LOW' | 'ALL'>('ALL');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<OpportunityListResponse | null>(null);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchOpportunities({
        tenant_id: tenantId,
        min_impressions: minImpressions === '' ? 10 : minImpressions,
        level: levelFilter === 'ALL' ? undefined : levelFilter,
        limit: 50,
      });
      setData(res.data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h5">SEO Opportunity Ranking</Typography>
      <Typography variant="body2" color="text.secondary">
        Pages ranked by improvement opportunity using OCI-based analysis. Low OCI + high traffic = best opportunities for quick wins.
      </Typography>

      <Alert severity="info" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" gutterBottom>How Opportunity Scoring Works</Typography>
        <Typography variant="body2">
          <strong>Formula:</strong> opportunity_score = (100 - OCI) × impression_weight + position_gap + CTR_gap
          <br />
          <strong>Best Opportunities:</strong> Low OCI (easier to rank) + High impressions (bigger impact) + Position 4-20 (close to top 3)
          <br />
          <strong>Min Impressions Filter:</strong> Higher values (100+) = focus on high-traffic pages only | Lower values (10-50) = include more pages with moderate traffic | Use 20+ for balanced results
        </Typography>
      </Alert>

      {/* Filters */}
      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              label="Min Daily Impressions"
              type="number"
              size="small"
              value={minImpressions}
              onChange={(e) => setMinImpressions(e.target.value === '' ? '' : Number(e.target.value))}
              sx={{ width: 200 }}
              helperText="10=all pages | 20-50=balanced | 100+=high-traffic only"
            />
            <TextField
              select
              label="Opportunity Level"
              size="small"
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value as 'HIGH' | 'MEDIUM' | 'LOW' | 'ALL')}
              sx={{ width: 180 }}
              SelectProps={{ native: true }}
            >
              <option value="ALL">All Levels</option>
              <option value="HIGH">High (67-100)</option>
              <option value="MEDIUM">Medium (33-66)</option>
              <option value="LOW">Low (0-33)</option>
            </TextField>
            <Button variant="contained" onClick={load} disabled={loading}>
              Load
            </Button>
            <Button variant="text" onClick={() => { setMinImpressions(10); setLevelFilter('ALL'); }}>
              Reset
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1">
              Opportunities
              {data && data.opportunities && data.opportunities.length > 0 && (
                <>
                  {' - '}
                  <Box component="span" sx={{ fontWeight: 'normal', fontSize: '0.9rem' }}>
                    {data.opportunities.length} 
                    {levelFilter !== 'ALL' && ` for ${levelFilter}`}
                    {', '}≥{minImpressions || 10} impressions
                  </Box>
                </>
              )}
            </Typography>
            {data && data.opportunities && data.opportunities.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {levelFilter === 'ALL' ? (
                  <>
                    HIGH: {data.opportunities.filter(o => o.opportunity_level === 'HIGH').length} | 
                    MEDIUM: {data.opportunities.filter(o => o.opportunity_level === 'MEDIUM').length} | 
                    LOW: {data.opportunities.filter(o => o.opportunity_level === 'LOW').length}
                  </>
                ) : (
                  `Level: ${levelFilter}`
                )}
              </Typography>
            )}
          </Box>
          
          <Divider sx={{ mb: 2 }} />
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <CircularProgress size={20} />
              <Typography>Loading…</Typography>
            </Stack>
          )}
          {!loading && (!data?.opportunities || data.opportunities.length === 0) && (
            <Typography>No opportunities found. Try adjusting filters or compute opportunity scores first.</Typography>
          )}
          {!loading && data?.opportunities && data.opportunities.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>Page URL</TableCell>
                    <TableCell align="right">Score</TableCell>
                    <TableCell align="right">OCI</TableCell>
                    <TableCell align="right">Avg Position</TableCell>
                    <TableCell align="right">Daily Impressions</TableCell>
                    <TableCell align="right">CTR</TableCell>
                    <TableCell>Level</TableCell>
                    <TableCell width={50} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.opportunities.map((opp, idx) => (
                    <>
                      <TableRow key={opp.id ?? idx} hover>
                        <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <a href={opp.page_url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none' }}>
                            {opp.page_url}
                          </a>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: opp.opportunity_score >= 67 ? '#d32f2f' : opp.opportunity_score >= 33 ? '#ed6c02' : '#666' }}>
                          {opp.opportunity_score.toFixed(1)}
                        </TableCell>
                        <TableCell align="right">{opp.oci_score.toFixed(1)}</TableCell>
                        <TableCell align="right">{opp.avg_position.toFixed(1)}</TableCell>
                        <TableCell align="right">{opp.avg_impressions.toFixed(0)}</TableCell>
                        <TableCell align="right">{(opp.avg_ctr * 100).toFixed(2)}%</TableCell>
                        <TableCell>
                          <Chip
                            label={opp.opportunity_level}
                            size="small"
                            color={opp.opportunity_level === 'HIGH' ? 'error' : opp.opportunity_level === 'MEDIUM' ? 'warning' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => setExpandedRow(expandedRow === idx ? null : idx)}
                          >
                            {expandedRow === idx ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      
                      {/* Expanded Factor Details */}
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                          <Collapse in={expandedRow === idx} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2, p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                                📊 Opportunity Factors Breakdown
                              </Typography>
                              
                              {opp.factors && (
                                <Grid container spacing={2}>
                                  {/* CTR Gap */}
                                  {opp.factors.ctr_gap && (
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Card variant="outlined">
                                        <CardContent>
                                          <Typography variant="caption" color="text.secondary">CTR Gap</Typography>
                                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                            {opp.factors.ctr_gap.contribution.toFixed(2)}
                                          </Typography>
                                          <Divider sx={{ my: 1 }} />
                                          <Stack spacing={0.5}>
                                            <Typography variant="caption">
                                              Raw: {opp.factors.ctr_gap.raw_value.toFixed(4)}
                                            </Typography>
                                            <Typography variant="caption">
                                              Normalized: {opp.factors.ctr_gap.normalized.toFixed(2)}
                                            </Typography>
                                            <Typography variant="caption">
                                              Weight: {opp.factors.ctr_gap.weight}
                                            </Typography>
                                            <Chip 
                                              label={opp.factors.ctr_gap.interpretation} 
                                              size="small" 
                                              sx={{ mt: 0.5 }}
                                              color={opp.factors.ctr_gap.interpretation === 'High' ? 'success' : opp.factors.ctr_gap.interpretation === 'Medium' ? 'warning' : 'default'}
                                            />
                                          </Stack>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  )}
                                  
                                  {/* OCI Inverse */}
                                  {opp.factors.oci_inverse && (
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Card variant="outlined">
                                        <CardContent>
                                          <Typography variant="caption" color="text.secondary">OCI Inverse (Competition)</Typography>
                                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                                            {opp.factors.oci_inverse.contribution.toFixed(2)}
                                          </Typography>
                                          <Divider sx={{ my: 1 }} />
                                          <Stack spacing={0.5}>
                                            <Typography variant="caption">
                                              Raw: {opp.factors.oci_inverse.raw_value.toFixed(4)}
                                            </Typography>
                                            <Typography variant="caption">
                                              Normalized: {opp.factors.oci_inverse.normalized.toFixed(2)}
                                            </Typography>
                                            <Typography variant="caption">
                                              Weight: {opp.factors.oci_inverse.weight}
                                            </Typography>
                                            <Chip 
                                              label={opp.factors.oci_inverse.interpretation} 
                                              size="small" 
                                              sx={{ mt: 0.5 }}
                                              color={opp.factors.oci_inverse.interpretation === 'High' ? 'success' : opp.factors.oci_inverse.interpretation === 'Medium' ? 'warning' : 'default'}
                                            />
                                          </Stack>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  )}
                                  
                                  {/* Position Gap */}
                                  {opp.factors.position_gap && (
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Card variant="outlined">
                                        <CardContent>
                                          <Typography variant="caption" color="text.secondary">Position Gap</Typography>
                                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#ed6c02' }}>
                                            {opp.factors.position_gap.contribution.toFixed(2)}
                                          </Typography>
                                          <Divider sx={{ my: 1 }} />
                                          <Stack spacing={0.5}>
                                            <Typography variant="caption">
                                              Raw: {opp.factors.position_gap.raw_value.toFixed(2)}
                                            </Typography>
                                            <Typography variant="caption">
                                              Normalized: {opp.factors.position_gap.normalized.toFixed(2)}
                                            </Typography>
                                            <Typography variant="caption">
                                              Weight: {opp.factors.position_gap.weight}
                                            </Typography>
                                            <Chip 
                                              label={opp.factors.position_gap.interpretation} 
                                              size="small" 
                                              sx={{ mt: 0.5 }}
                                              color={opp.factors.position_gap.interpretation === 'High' ? 'success' : opp.factors.position_gap.interpretation === 'Medium' ? 'warning' : 'default'}
                                            />
                                          </Stack>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  )}
                                  
                                  {/* Traffic Potential */}
                                  {opp.factors.traffic_potential && (
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Card variant="outlined">
                                        <CardContent>
                                          <Typography variant="caption" color="text.secondary">Traffic Potential</Typography>
                                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                            {opp.factors.traffic_potential.contribution.toFixed(2)}
                                          </Typography>
                                          <Divider sx={{ my: 1 }} />
                                          <Stack spacing={0.5}>
                                            <Typography variant="caption">
                                              Raw: {opp.factors.traffic_potential.raw_value.toFixed(2)}
                                            </Typography>
                                            <Typography variant="caption">
                                              Normalized: {opp.factors.traffic_potential.normalized.toFixed(2)}
                                            </Typography>
                                            <Typography variant="caption">
                                              Weight: {opp.factors.traffic_potential.weight}
                                            </Typography>
                                            <Chip 
                                              label={opp.factors.traffic_potential.interpretation} 
                                              size="small" 
                                              sx={{ mt: 0.5 }}
                                              color={opp.factors.traffic_potential.interpretation === 'High' ? 'success' : opp.factors.traffic_potential.interpretation === 'Medium' ? 'warning' : 'default'}
                                            />
                                          </Stack>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  )}
                                </Grid>
                              )}
                              
                              <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="caption">
                                  <strong>How to read:</strong> Each factor shows its contribution to the final opportunity score. 
                                  Higher contribution = bigger impact on ranking this as an opportunity.
                                </Typography>
                              </Alert>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {data?.total_count !== undefined && (
            <Typography variant="caption" sx={{ mt: 2 }}>
              Showing {data.opportunities?.length ?? 0} of {data.total_count} opportunities
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Guidance */}
      <Card>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>Understanding the Metrics</Typography>
          <Stack spacing={1} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Opportunity Score (0-100):</strong> Higher = better opportunity. Combines low competition (inverse OCI), high traffic potential, position gaps, and CTR gaps.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>OCI Score:</strong> Observed Competitiveness Index (0-100). Lower OCI = easier to rank = better opportunity.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Level:</strong> HIGH (67-100) = prime opportunity | MEDIUM (33-66) = moderate | LOW (0-33) = limited upside
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Recommended Action:</strong> Focus on HIGH opportunities with position 4-20 and &gt;100 daily impressions for quick wins.
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
